import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, updateDoc, deleteDoc, doc, increment, getDocs, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { db } from '../firebase';
import { openDocument, saveDocument, isNative } from '../native';
import { SUBJECTS } from '../constants';
import { Icon } from './ui';
import { toast, confirmSheet } from './feedback';
import { REFUSED } from '../sendErrors';
import { useBlocked, withoutBlocked } from '../blocking';

const CATS = ['USMLE STEP 1','USMLE STEP 2','USMLE STEP 3','General Study'];
const MAX_MB = 25;
const MAX_RECIPIENTS = 10;
const STORAGE_CAP_BYTES = 250 * 1024 * 1024; // 250 MB per user
const byNewest = arr => [...arr].sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));

// "Cardio notes.pdf" → "Cardio notes".
const fileTitle = name => (name || 'Document').replace(/\.[^.]+$/, '').trim() || name;
// Phones and tablets say "Tap", mice say "Click" (design F15).
const TOUCH = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

function StudyMaterials({ user, userProfile, showNotification, onFindMatches }) {
  const [allShared, setShared] = useState([]);
  const [myUploads, setMyUploads] = useState([]);
  const [allContacts, setContacts] = useState([]);
  const [tab, setTab] = useState('shared');
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({title:'',description:'',category:'',subject:'',fileType:'PDF',size:''});
  const [recipients, setRecipients] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(0);
  const [uploadsLoaded, setUploadsLoaded] = useState(false);
  const [sharedLoaded, setSharedLoaded] = useState(false);
  const storageChecked = useRef(false);
  // Nothing from, and no sharing with, people I've blocked.
  const blocked = useBlocked(user?.uid);
  const shared = withoutBlocked(allShared, blocked, m => m.uploadedByUid);
  const contacts = withoutBlocked(allContacts, blocked, c => c.uid);

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const map = {};
      try {
        const recv = await getDocs(query(collection(db,'messages'), where('toUid','==',user.uid)));
        recv.docs.forEach(d => { const m=d.data(); if (m.fromUid && m.fromUid !== user.uid) map[m.fromUid] = m.fromName || 'Study partner'; });
      } catch(e) { console.warn('messages lookup failed', e); }
      try {
        const convos = await getDocs(query(collection(db,'conversations'), where('participants','array-contains',user.uid)));
        convos.docs.forEach(d => { const c=d.data();
          if (c.fromUid && c.fromUid !== user.uid) map[c.fromUid] = c.fromName || 'Study partner';
          if (c.toUid && c.toUid !== user.uid) map[c.toUid] = c.toName || 'Study partner';
        });
      } catch(e) { console.warn('conversations lookup failed', e); }
      setContacts(Object.entries(map).map(([uid,name])=>({uid,name})).sort((a,b)=>a.name.localeCompare(b.name)));
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'materials'), where('sharedWith','array-contains',user.uid));
    return onSnapshot(q, snap => { setShared(byNewest(snap.docs.map(d=>({id:d.id,...d.data()})))); setSharedLoaded(true); });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'materials'), where('uploadedByUid','==',user.uid));
    return onSnapshot(q, snap => { setMyUploads(byNewest(snap.docs.map(d=>({id:d.id,...d.data()})))); setUploadsLoaded(true); });
  }, [user?.uid]);

  // Older uploads predate the storage counter; recount once from the user's own files.
  useEffect(() => {
    if (!uploadsLoaded || !userProfile || storageChecked.current) return;
    storageChecked.current = true;
    (async () => {
      let total = 0;
      for (const m of myUploads) {
        let size = m.fileSizeBytes;
        if (!size && m.storagePath) {
          try { size = (await getMetadata(ref(getStorage(), m.storagePath))).size; } catch (e) { console.warn('size lookup failed', e); }
        }
        total += size || 0;
      }
      if (total !== (userProfile.storageUsedBytes || 0)) {
        try { await updateDoc(doc(db, 'users', user.uid), { storageUsedBytes: total }); }
        catch (e) { console.warn('storage recount failed', e); }
      }
    })();
  }, [uploadsLoaded, myUploads, userProfile, user.uid]);

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast('That file is ' + (f.size/1024/1024).toFixed(1) + ' MB. The maximum is ' + MAX_MB + ' MB.', { type: 'error' });
      e.target.value = '';
      return;
    }
    const used = userProfile?.storageUsedBytes || 0;
    const remaining = STORAGE_CAP_BYTES - used;
    if (f.size > remaining) {
      toast(`Not enough storage. You have ${(remaining / 1024 / 1024).toFixed(1)} MB remaining of your 250 MB cap.`, { type: 'error' });
      e.target.value = '';
      return;
    }
    setFile(f);
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    // The type comes from the file itself (design I6); there's no File Type field.
    const typeMap = { pdf:'PDF', doc:'DOCX', docx:'DOCX', ppt:'PPTX', pptx:'PPTX', apkg:'Anki Deck', zip:'ZIP', txt:'Text', jpg:'Photo', jpeg:'Photo', png:'Photo', heic:'Photo' };
    setForm(p => ({
      ...p,
      fileType: typeMap[ext] || 'Other',
      size: f.size < 1024*1024 ? Math.round(f.size/1024) + ' KB' : (f.size/1024/1024).toFixed(1) + ' MB'
    }));
  };

  // At most 10 people per document (the database rules check each one for blocks).
  const toggleRecipient = (uid) => {
    if (!recipients.includes(uid) && recipients.length >= MAX_RECIPIENTS) { toast(`You can share a document with up to ${MAX_RECIPIENTS} people at a time.`, { type: 'error' }); return; }
    setRecipients(p => p.includes(uid) ? p.filter(x=>x!==uid) : [...p, uid]);
  };

  const closeUpload = () => { if (uploading) return; setShowUpload(false); setFile(null); setRecipients([]); };

  const upload = async () => {
    // Only a person and a file are required (design F15); the title defaults to the file name.
    if (!recipients.length) { toast('Choose at least one study partner to share with.', { type: 'error' }); return; }
    if (!file) { toast('Please choose a file.', { type: 'error' }); return; }
    const used = userProfile?.storageUsedBytes || 0;
    if (used + file.size > STORAGE_CAP_BYTES) {
      toast('Storage cap reached (250 MB). Delete some documents to free space.', { type: 'error' });
      return;
    }
    setUploading(true); setPct(0);
    const storage = getStorage();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,'_');
    const path = `materials/${user.uid}/${Date.now()}-${safeName}`;
    let fileUploaded = false;
    try {
      const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type || 'application/octet-stream' });
      await new Promise((resolve, reject) => {
        task.on('state_changed', s => setPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)), reject, resolve);
      });
      fileUploaded = true;
      const fileUrl = await getDownloadURL(task.snapshot.ref);
      const names = recipients.map(uid => (contacts.find(c=>c.uid===uid)||{}).name).filter(Boolean);
      await addDoc(collection(db,'materials'),{
        ...form, title: form.title.trim() || fileTitle(file.name), fileUrl, storagePath: path, fileName: file.name,
        fileSizeBytes: file.size,
        uploadedByUid: user.uid,
        uploadedByName: userProfile?.name || 'Anonymous',
        sharedWith: recipients,
        sharedWithNames: names,
        downloads: 0,
        createdAt: serverTimestamp()
      });
      try {
        await updateDoc(doc(db, 'users', user.uid), { storageUsedBytes: increment(file.size) });
      } catch (e) { console.warn('storage counter update failed', e); }
      const senderName = userProfile?.name || 'A study partner';
      for (const recipUid of recipients) {
        try {
          const recipSnap = await getDoc(doc(db,'users',recipUid));
          if (recipSnap.exists() && recipSnap.data()?.notificationPreferences?.newMessages !== false) {
            await addDoc(collection(db,'notifications'),{
              toUid:recipUid, fromUid:user.uid, fromName:senderName,
              type:'document_shared', text:`${senderName} shared a document with you`,
              read:false, createdAt:serverTimestamp()
            });
          }
        } catch(e) { console.warn('notif write failed',e); }
      }
      showNotification('Shared!', `Sent to ${names.join(', ')}.`);
      setShowUpload(false);
      setForm({title:'',description:'',category:'',subject:'',fileType:'PDF',size:''});
      setFile(null); setRecipients([]);
    } catch(e) {
      console.error(e);
      const code = e?.code || '';
      // The file went up but the share was refused: don't leave it behind.
      if (fileUploaded && code === 'permission-denied') deleteObject(ref(storage, path)).catch(() => {});
      if (code.includes('unauthorized')) toast('Upload failed: you don\'t have permission to upload files. Try logging out and back in.', { type: 'error' });
      else if (code.includes('quota-exceeded')) toast('Upload failed: storage quota exceeded. Please contact support.', { type: 'error' });
      else if (code.includes('canceled')) toast('Upload canceled.', { type: 'info' });
      else if (code === 'permission-denied') toast(REFUSED.share);
      else if (code.includes('network')) toast('Upload failed: network error. Check your connection and try again.', { type: 'error' });
      else toast('Upload failed. Please try again — if it keeps happening, email support@medxmeet.com.', { type: 'error' });
    }
    finally { setUploading(false); setPct(0); }
  };

  const download = async (m) => {
    if (!m.fileUrl) { toast('No file attached to this item.', { type: 'error' }); return; }
    // iPhone app: iOS's own viewer (brief 028); the website opens the file in a tab.
    const shown = await openDocument(m.fileUrl, m.fileName, m.storagePath).catch(e => { console.warn(e); toast('Could not open this document. Please try again.', { type: 'error' }); return null; });
    if (shown === null) return;
    if (!shown) window.open(m.fileUrl, '_blank');
    try { await updateDoc(doc(db,'materials',m.id),{downloads:increment(1)}); } catch(e){}
  };

  const remove = async (m) => {
    if (!await confirmSheet({ title: 'Delete this document?', message: `"${m.title}" will be removed for you and everyone you shared it with. This cannot be undone.`, confirmLabel: 'Delete', danger: true })) return;
    try {
      if (m.storagePath) { try { await deleteObject(ref(getStorage(), m.storagePath)); } catch(e){ console.warn(e); } }
      await deleteDoc(doc(db,'materials',m.id));
      showNotification('Deleted','The document has been removed.');
      if (m.fileSizeBytes) {
        try {
          await updateDoc(doc(db, 'users', user.uid), { storageUsedBytes: increment(-m.fileSizeBytes) });
        } catch (e) { console.warn('storage counter decrement failed', e); }
      }
    } catch(e) {
      const code = e?.code || '';
      if (code.includes('unauthorized')) toast('Delete failed: you can only delete your own documents.', { type: 'error' });
      else if (code.includes('network')) toast('Delete failed: network error. Check your connection and try again.', { type: 'error' });
      else toast('Delete failed. Please try again — if it keeps happening, email support@medxmeet.com.', { type: 'error' });
    }
  };

  const fmt = ts => ts?.toDate ? `${Math.floor((new Date()-ts.toDate())/86400000)||0}d ago` : 'Just now';

  // The whole card opens the file (design F14); download counts only on your own uploads.
  const Card = ({m,own}) => (
    <div className="doc-card tappable" onClick={()=>download(m)}>
      <div className="doc-type">{m.fileType}</div>
      <h3 className="doc-title"><button type="button" className="doc-open" onClick={e=>{ e.stopPropagation(); download(m); }}>{m.title}</button></h3>
      {m.description && <p className="doc-desc">{m.description}</p>}
      <div className="doc-meta">
        {m.subject&&<span>{m.subject}</span>}{m.size&&<span>{m.size}</span>}{own&&<span>{m.downloads||0} download{(m.downloads||0)===1?'':'s'}</span>}
      </div>
      <div className="doc-actions">
        <button className="btn-icon small" onClick={e=>{ e.stopPropagation(); if (isNative) saveDocument(m.fileUrl, m.fileName, m.storagePath).catch(() => toast('Could not save this document. Please try again.', { type: 'error' })); else download(m); }} aria-label={isNative ? 'Save or share' : 'Download'}><Icon name="download" size={20} /></button>
        {own && <button className="btn-icon small danger" onClick={e=>{ e.stopPropagation(); remove(m); }} aria-label="Delete"><Icon name="trash" size={20} /></button>}
      </div>
      <div className="doc-foot">
        <span>{own ? `Shared with: ${(m.sharedWithNames||[]).join(', ')||'—'}` : `From ${m.uploadedByName}`}</span>
        <span>{fmt(m.createdAt)}</span>
      </div>
    </div>
  );

  const Empty = ({icon,title,sub,action,onAction}) => (
    <div className="card empty-state">
      <div className="empty-icon"><Icon name={icon} size={40} /></div>
      <h3>{title}</h3>
      <p className="small">{sub}</p>
      {onAction && <button className="btn-small secondary" style={{marginTop:'16px'}} onClick={onAction}>{action}</button>}
    </div>
  );

  return (
    <>
      <div className="card-head">
        <p className="muted small" style={{alignSelf:'center'}}>Private sharing between you and your study partners</p>
        <button className="btn" onClick={()=>setShowUpload(true)}><Icon name="upload" size={20} />Share a Document</button>
      </div>

      <div className="segmented" role="tablist">
        {/* Counts only once loaded, so no "(0)" flash (review R8). */}
        {[['shared',sharedLoaded?`Shared with me (${shared.length})`:'Shared with me'],['mine',uploadsLoaded?`I shared (${myUploads.length})`:'I shared']].map(([t,l])=>(
          <button key={t} role="tab" aria-selected={tab===t} className={tab===t?'active':''} onClick={()=>setTab(t)}>{l}</button>
        ))}
      </div>

      {tab==='shared' && sharedLoaded && (shared.length===0
        ? <Empty icon="download" title="Nothing shared with you yet" sub="Documents your study partners share will appear here." action="Find a partner" onAction={onFindMatches} />
        : shared.map(m=><Card key={m.id} m={m} own={false} />))}

      {tab==='mine' && (()=>{
        const usedBytes = userProfile?.storageUsedBytes || 0;
        const usedMB = (usedBytes / 1024 / 1024).toFixed(1);
        const pctUsed = Math.min(100, (usedBytes / STORAGE_CAP_BYTES) * 100);
        const level = pctUsed >= 90 ? 'full' : pctUsed >= 70 ? 'warn' : '';
        return (<>
          <div className="card storage-box">
            <div className="storage-box-head">
              <span>Storage used</span>
              <span>{usedMB} / 250 MB</span>
            </div>
            <div className="storage-track" role="progressbar" aria-label="Storage used" aria-valuenow={Math.round(pctUsed)} aria-valuemin={0} aria-valuemax={100}>
              <div className={`storage-fill ${level}`} style={{ width: `${pctUsed}%` }} />
            </div>
          </div>
          {!uploadsLoaded ? null : myUploads.length === 0
            ? <Empty icon="upload" title="You haven't shared anything" sub="Share notes and decks privately with your study partners." action="Share a document" onAction={()=>setShowUpload(true)} />
            : myUploads.map(m => <Card key={m.id} m={m} own={true} />)}
        </>);
      })()}

      {showUpload&&(
        <div className="modal-overlay" onClick={closeUpload}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Share a Document</h3>
              <button className="modal-close" onClick={closeUpload} aria-label="Close">×</button>
            </div>

            <div className="form-group">
              <label>Share with * (people you've messaged)</label>
              {contacts.length === 0
                ? <div className="note-box">No study partners yet. Message someone from Find Matches first.</div>
                : <div className="chip-toggles">
                    {contacts.map(c=>{
                      const on=recipients.includes(c.uid);
                      return (
                        <button key={c.uid} type="button" className={`chip toggle ${on?'on':''}`} aria-pressed={on} onClick={()=>toggleRecipient(c.uid)}>
                          {on&&<Icon name="check" size={16} />}{c.name}
                        </button>
                      );
                    })}
                  </div>}
            </div>

            <div className="form-group">
              <label>File * (max {MAX_MB} MB)</label>
              <button type="button" className={`file-drop ${file?'has-file':''}`} onClick={()=>document.getElementById('material-file').click()}>
                <div className="file-drop-icon"><Icon name={file?'check':'docs'} size={32} /></div>
                <div style={{fontWeight:600,wordBreak:'break-all'}}>{file ? file.name : `${TOUCH ? 'Tap' : 'Click'} to choose a file`}</div>
                <div className="small muted" style={{marginTop:'4px'}}>{file ? `${form.fileType} · ${form.size}` : 'PDF, DOCX, PPTX, Anki, ZIP, TXT, photos'}</div>
              </button>
              <input id="material-file" type="file" style={{display:'none'}} accept=".pdf,.doc,.docx,.ppt,.pptx,.apkg,.zip,.txt,.jpg,.jpeg,.png,.heic" onChange={pickFile} />
            </div>

            <div className="form-group"><label>Title (optional)</label><input type="text" placeholder={file ? fileTitle(file.name) : 'Uses the file name'} value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} /></div>
            <div className="form-group"><label>Description (optional)</label><textarea placeholder="What's in it?" rows="2" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} style={{resize:'vertical'}} /></div>
            <div className="grid-2">
              <div className="form-group" style={{marginBottom:0}}><label>Category (optional)</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}><option value="">Select</option>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
              <div className="form-group" style={{marginBottom:0}}><label>Subject (optional)</label><select value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))}><option value="">Select</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}</select></div>
            </div>

            {uploading && (
              <div style={{marginTop:'16px'}}>
                <div className="storage-track"><div className="storage-fill" style={{width:`${pct}%`}} /></div>
                <div className="small muted" style={{textAlign:'center',marginTop:'6px'}}>{pct}%</div>
              </div>
            )}

            <button className="btn full" style={{marginTop:'16px'}} onClick={upload} disabled={uploading}>{uploading?'Uploading...':'Share Document'}</button>
          </div>
        </div>
      )}
    </>
  );
}
export default StudyMaterials;